import { useState } from "react";
import { api, errDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PlugsConnected, DownloadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export const EXCHANGE_META = {
  bybit: { label: "Bybit", needsPassphrase: false, testnetLabel: "Testnet" },
  binance: { label: "Binance (USDT-M Futures)", needsPassphrase: false, testnetLabel: "Testnet" },
  okx: { label: "OKX", needsPassphrase: true, testnetLabel: "Demo Trading", hasRegion: true },
};

const OKX_REGIONS = [
  { id: "global", label: "Global (www.okx.com)" },
  { id: "eea", label: "Europe / EEA (my.okx.com login)" },
  { id: "us", label: "United States (us.okx.com)" },
];

export default function ExchangeSection({ exchange, cfg, globalLive, onChanged }) {
  const meta = EXCHANGE_META[exchange];
  const [form, setForm] = useState({ api_key: "", api_secret: "", api_passphrase: "", environment: "mainnet", region: "global" });
  const [busy, setBusy] = useState("");

  const saveKeys = async () => {
    if (!form.api_key || !form.api_secret) {
      toast.error("API key and secret are required");
      return;
    }
    if (meta.needsPassphrase && !form.api_passphrase) {
      toast.error(`${meta.label} requires an API passphrase`);
      return;
    }
    setBusy("save");
    try {
      await api.post("/exchanges", {
        exchange,
        api_key: form.api_key.trim(),
        api_secret: form.api_secret.trim(),
        api_passphrase: form.api_passphrase.trim() || null,
        environment: form.environment,
        region: form.region,
      });
      toast.success(`${meta.label} keys saved (encrypted). Exchange starts in read-only mode.`);
      setForm({ api_key: "", api_secret: "", api_passphrase: "", environment: form.environment, region: form.region });
      onChanged();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy("");
    }
  };

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy("");
    }
  };

  const testConnection = () => run("test", async () => {
    const { data } = await api.post(`/exchanges/${exchange}/test`);
    toast.success(`Connected. ${data.balances?.length || 0} coin balance(s) found.`);
  });

  const importTrades = () => run("import", async () => {
    const { data } = await api.post(`/exchanges/${exchange}/import`);
    toast.success(`Imported ${data.positions} position(s), ${data.orders} pending order(s), ${data.spot} spot trade(s), ${data.closed} closed trade(s). Skipped ${data.skipped}.`);
    onChanged();
  });

  const toggleLive = (live) => run("toggle", async () => {
    await api.put(`/exchanges/${exchange}`, { trading_mode: live ? "live" : "readonly" });
    toast.success(live ? `${meta.label} LIVE trading enabled` : `${meta.label} set to read-only`);
    onChanged();
  });

  const removeExchange = () => run("remove", async () => {
    await api.delete(`/exchanges/${exchange}`);
    toast.success(`${meta.label} configuration removed`);
    onChanged();
  });

  return (
    <section className="border border-border bg-card" data-testid={`exchange-section-${exchange}`}>
      <div className="px-5 py-3 border-b border-border label-caps text-muted-foreground">
        Exchange — {meta.label}
      </div>
      <div className="p-5 space-y-4">
        {cfg ? (
          <div className="space-y-4" data-testid={`${exchange}-configured-panel`}>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="font-mono text-sm">API Key: {cfg.key_preview}</div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="label-caps">{cfg.environment}</Badge>
                  {meta.hasRegion && (
                    <Badge variant="outline" className="label-caps" data-testid={`${exchange}-region-badge`}>
                      {OKX_REGIONS.find((r) => r.id === (cfg.region || "global"))?.label.split(" (")[0]}
                    </Badge>
                  )}
                  <Badge variant="outline" data-testid={`${exchange}-mode-badge`}
                    className={`label-caps ${cfg.trading_mode === "live" ? "border-[hsl(var(--profit))] text-profit" : ""}`}>
                    {cfg.trading_mode}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Live trading</span>
                <Switch data-testid={`${exchange}-live-switch`} checked={cfg.trading_mode === "live"}
                  onCheckedChange={toggleLive} disabled={!globalLive} />
              </div>
            </div>
            {!globalLive && (
              <p className="text-xs text-loss">Global TRADING_MODE is readonly — live trading cannot be enabled per-exchange.</p>
            )}
            <div className="flex gap-2">
              <Button data-testid={`${exchange}-test-btn`} variant="outline" size="sm" onClick={testConnection} disabled={busy === "test"}>
                <PlugsConnected size={14} className="mr-1" /> {busy === "test" ? "Testing…" : "Test Connection"}
              </Button>
              <Button data-testid={`${exchange}-import-btn`} variant="outline" size="sm" onClick={importTrades} disabled={busy === "import"}>
                <DownloadSimple size={14} className="mr-1" /> {busy === "import" ? "Importing…" : "Import Trades"}
              </Button>
              <Button data-testid={`${exchange}-remove-btn`} variant="outline" size="sm" onClick={removeExchange}
                className="text-loss hover:text-loss">
                <Trash size={14} className="mr-1" /> Remove
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">To replace keys, enter new ones below and save.</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid={`${exchange}-not-configured-msg`}>
            No {meta.label} keys configured. Keys are encrypted at rest and never sent to the browser.
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="col-span-2 md:col-span-1">
            <Label className="label-caps text-muted-foreground">API Key</Label>
            <Input data-testid={`${exchange}-key-input`} value={form.api_key} className="font-mono mt-1"
              onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label className="label-caps text-muted-foreground">API Secret</Label>
            <Input data-testid={`${exchange}-secret-input`} type="password" value={form.api_secret} className="font-mono mt-1"
              onChange={(e) => setForm({ ...form, api_secret: e.target.value })} />
          </div>
          {meta.needsPassphrase && (
            <div className="col-span-2 md:col-span-1">
              <Label className="label-caps text-muted-foreground">API Passphrase</Label>
              <Input data-testid={`${exchange}-passphrase-input`} type="password" value={form.api_passphrase} className="font-mono mt-1"
                onChange={(e) => setForm({ ...form, api_passphrase: e.target.value })} />
            </div>
          )}
          {meta.hasRegion && (
            <div className="col-span-2 md:col-span-1">
              <Label className="label-caps text-muted-foreground">Account region</Label>
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger data-testid={`${exchange}-region-select`} className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OKX_REGIONS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                EU accounts (login via my.okx.com) must pick Europe / EEA — global keys give "API key doesn't exist".
              </p>
            </div>
          )}
          <div>
            <Label className="label-caps text-muted-foreground">Environment</Label>
            <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
              <SelectTrigger data-testid={`${exchange}-env-select`} className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mainnet">Mainnet</SelectItem>
                <SelectItem value="testnet">{meta.testnetLabel}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid={`${exchange}-save-btn`} onClick={saveKeys} disabled={busy === "save"}
              className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
              {busy === "save" ? "Saving…" : "Save Keys"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
